import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getAdminSettings,
  getCalendarSettings,
  listSiteContent,
  updateAdminSettings,
  updateCalendarSettings,
  getGoogleCalendarStatus,
  syncGoogleCalendarNow,
  checkSlotAvailability,
  updateSiteContent,
} from "@/lib/admin-extra.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: SettingsPage,
});

type Practice = {
  practice_name: string;
  practice_email: string;
  practice_phone: string;
  whatsapp: string;
  address: string;
  session_timeout_minutes: number;
  notify_on_booking: boolean;
  notify_on_order: boolean;
};

type Content = { id: string; key: string; label: string; value: string; group_name: string };

function SettingsPage() {
  return (
    <Tabs defaultValue="practice" className="space-y-4">
      <TabsList>
        <TabsTrigger value="practice">Practice</TabsTrigger>
        <TabsTrigger value="calendar">Calendar sync</TabsTrigger>
        <TabsTrigger value="content">Website content</TabsTrigger>
      </TabsList>
      <TabsContent value="practice">
        <PracticeSettings />
      </TabsContent>
      <TabsContent value="calendar">
        <CalendarSettings />
      </TabsContent>
      <TabsContent value="content">
        <SiteContent />
      </TabsContent>
    </Tabs>
  );
}

function CalendarSettings() {
  const qc = useQueryClient();
  const get = useServerFn(getCalendarSettings);
  const update = useServerFn(updateCalendarSettings);
  const { data, isPending } = useQuery({ queryKey: ["admin", "calendar"], queryFn: () => get() });
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (data) setUrl(data.external_ics_url);
  }, [data]);

  const mut = useMutation({
    mutationFn: (p: { external_ics_url: string; rotate_token?: boolean }) => update({ data: p }),
    onSuccess: () => {
      toast.success("Calendar settings saved");
      qc.invalidateQueries({ queryKey: ["admin", "calendar"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending || !data) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const feedUrl = `${origin}/api/public/calendar/bookings.ics?token=${data.feed_token}`;
  const feedReady = data.feed_token.length > 0;

  return (
    <div className="space-y-4">
      <GoogleCalendarCard />
      <Card>
        <CardHeader>
          <CardTitle>Website bookings feed</CardTitle>
          <CardDescription>
            Private calendar link. Subscribe to it from NetPractice, Google Calendar, Outlook or Apple Calendar to see
            every website booking in your diary.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input readOnly value={feedUrl} onFocus={(e) => e.currentTarget.select()} />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!feedReady}
              onClick={() => {
                navigator.clipboard.writeText(feedUrl);
                toast.success("Feed link copied");
              }}
            >
              Copy link
            </Button>
            <Button variant="outline" size="sm" disabled={!feedReady} asChild>
              <a href={feedReady ? feedUrl : undefined} target="_blank" rel="noreferrer">Download .ics</a>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={mut.isPending}
              onClick={() => {
                if (confirm("Rotate the feed token? Existing calendar subscriptions will stop working.")) {
                  mut.mutate({ external_ics_url: url, rotate_token: true });
                }
              }}
            >
              Rotate token
            </Button>
          </div>
          {!feedReady && (
            <p className="text-xs text-destructive">
              Feed token is not ready yet. Refresh the page or save the calendar settings to generate one.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Keep this link private, anyone with it can read your booking diary.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Practice diary feed (NetPractice)</CardTitle>
          <CardDescription>
            Paste the private iCalendar (ICS) link from NetPractice or your other diary. Appointments in that diary are
            blocked out on the online booking page, so patients cannot double book you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Row label="External ICS feed URL">
            <Input
              value={url}
              placeholder="https://... .ics"
              onChange={(e) => setUrl(e.target.value)}
            />
          </Row>
          <Button disabled={mut.isPending} onClick={() => mut.mutate({ external_ics_url: url })}>
            {mut.isPending ? "Saving..." : "Save"}
          </Button>
          <p className="text-xs text-muted-foreground">
            The feed is refreshed every 5 minutes. If it is unreachable, online booking keeps working as normal (Google Calendar is still checked).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function PracticeSettings() {
  const qc = useQueryClient();
  const get = useServerFn(getAdminSettings);
  const update = useServerFn(updateAdminSettings);
  const { data, isPending } = useQuery({ queryKey: ["admin", "settings"], queryFn: () => get() });
  const [form, setForm] = useState<Practice | null>(null);

  useEffect(() => {
    if (data) setForm(data as Practice);
  }, [data]);

  const mut = useMutation({
    mutationFn: (p: Practice) => update({ data: p }),
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending || !form) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Practice details</CardTitle>
        <CardDescription>Used on notifications and admin emails.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="Practice name">
            <Input value={form.practice_name} onChange={(e) => setForm({ ...form, practice_name: e.target.value })} />
          </Row>
          <Row label="Email">
            <Input value={form.practice_email} onChange={(e) => setForm({ ...form, practice_email: e.target.value })} />
          </Row>
          <Row label="Phone">
            <Input value={form.practice_phone} onChange={(e) => setForm({ ...form, practice_phone: e.target.value })} />
          </Row>
          <Row label="WhatsApp">
            <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          </Row>
          <Row label="Session timeout (minutes)">
            <Input
              type="number"
              min={5}
              max={480}
              value={form.session_timeout_minutes}
              onChange={(e) => setForm({ ...form, session_timeout_minutes: Number(e.target.value) })}
            />
          </Row>
          <div className="sm:col-span-2">
            <Row label="Address">
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Row>
          </div>
        </div>

        <div className="space-y-3 rounded-lg border p-4">
          <Toggle
            label="Email me when a booking is made"
            checked={form.notify_on_booking}
            onChange={(v) => setForm({ ...form, notify_on_booking: v })}
          />
          <Toggle
            label="Email me when a shop order is placed"
            checked={form.notify_on_order}
            onChange={(v) => setForm({ ...form, notify_on_order: v })}
          />
        </div>

        <Button onClick={() => mut.mutate(form)} disabled={mut.isPending}>
          {mut.isPending ? "Saving..." : "Save settings"}
        </Button>
      </CardContent>
    </Card>
  );
}

function SiteContent() {
  const qc = useQueryClient();
  const list = useServerFn(listSiteContent);
  const update = useServerFn(updateSiteContent);
  const { data, isPending } = useQuery({ queryKey: ["admin", "site-content"], queryFn: () => list() });
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const mut = useMutation({
    mutationFn: (v: { id: string; value: string }) => update({ data: v }),
    onSuccess: () => {
      toast.success("Content updated");
      qc.invalidateQueries({ queryKey: ["admin", "site-content"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const rows = (data ?? []) as Content[];
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No editable website content has been set up yet.</p>;
  }

  const groups = Array.from(new Set(rows.map((r) => r.group_name)));

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <Card key={g}>
          <CardHeader>
            <CardTitle className="capitalize">{g}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {rows
              .filter((r) => r.group_name === g)
              .map((r) => {
                const value = drafts[r.id] ?? r.value;
                const dirty = value !== r.value;
                return (
                  <div key={r.id} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{r.label}</Label>
                    <Textarea
                      rows={value.length > 120 ? 3 : 1}
                      value={value}
                      onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })}
                    />
                    {dirty && (
                      <Button size="sm" onClick={() => mut.mutate({ id: r.id, value })} disabled={mut.isPending}>
                        Save
                      </Button>
                    )}
                  </div>
                );
              })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

const fmtSA = (iso: string) =>
  new Date(iso).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function GoogleCalendarCard() {
  const qc = useQueryClient();
  const get = useServerFn(getGoogleCalendarStatus);
  const sync = useServerFn(syncGoogleCalendarNow);
  const check = useServerFn(checkSlotAvailability);
  const { data, isPending, refetch, isFetching } = useQuery({ queryKey: ["admin", "gcal"], queryFn: () => get() });
  const [when, setWhen] = useState("");
  const [mins, setMins] = useState(60);
  const [result, setResult] = useState<null | { website: boolean; googleReachable: boolean; google: boolean | null }>(null);

  const syncMut = useMutation({
    mutationFn: () => sync(),
    onSuccess: (r) => {
      if (r.ok) toast.success(`Synchronised. ${r.cancelled} cancelled, ${r.moved} moved from Google.`);
      else toast.error("Synchronisation failed. Online booking stays paused until Google is reachable.");
      qc.invalidateQueries({ queryKey: ["admin", "gcal"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const checkMut = useMutation({
    mutationFn: () => {
      // Treat the typed time as South African time (UTC+2, no daylight saving).
      const iso = new Date(`${when}:00+02:00`).toISOString();
      return check({ data: { start: iso, minutes: mins } });
    },
    onSuccess: setResult,
    onError: (e: Error) => toast.error(e.message),
  });

  const st = data?.status.state;
  const label = !data
    ? ""
    : !data.connected
      ? st === "auth_expired" ? "Sign-in expired" : "Not connected"
      : syncMut.isPending || st === "synchronizing" ? "Synchronising"
      : st === "error" ? "Synchronisation error"
      : st === "ok" ? "Successfully synchronised" : "Connected";
  const bad = !data?.connected || st === "error" || st === "auth_expired";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Google Calendar (two-way)</CardTitle>
        <CardDescription>
          Anything in your Google Calendar blocks that time on the booking page. Website bookings are added to Google
          automatically. Patients only ever see available or unavailable times.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isPending || !data ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <>
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div><dt className="text-muted-foreground">Status</dt><dd className={bad ? "font-semibold text-destructive" : "font-semibold text-primary"}>{label}</dd></div>
              <div><dt className="text-muted-foreground">Connected account</dt><dd>{data.account ?? "info@nsphysiotherapy.co.za"}</dd></div>
              <div><dt className="text-muted-foreground">Calendar</dt><dd>{data.calendar ?? "Primary calendar"}</dd></div>
              <div><dt className="text-muted-foreground">Last successful sync</dt><dd>{data.status.last_success_at ? fmtSA(data.status.last_success_at) : "Not yet"}</dd></div>
            </dl>
            {(data.error || data.status.last_error) && bad && (
              <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                {data.error ?? data.status.last_error}. Online booking is paused until this is fixed.
                {(!data.connected || st === "auth_expired") && " Reconnect Google Calendar from Connectors in the Lovable editor (or ask me to reconnect it)."}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
                {isFetching ? "Testing..." : "Test connection"}
              </Button>
              <Button size="sm" disabled={syncMut.isPending} onClick={() => syncMut.mutate()}>
                {syncMut.isPending ? "Synchronising..." : "Sync now"}
              </Button>
            </div>

            <div>
              <h4 className="mb-2 text-sm font-semibold">Google busy times, next 7 days</h4>
              {data.busy.length === 0 ? (
                <p className="text-sm text-muted-foreground">No busy times in Google Calendar.</p>
              ) : (
                <ul className="max-h-48 space-y-1 overflow-auto text-sm">
                  {data.busy.map((b) => (
                    <li key={b.start} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-accent-foreground" aria-hidden /> {fmtSA(b.start)} to {new Date(b.end).toLocaleTimeString("en-ZA", { timeZone: "Africa/Johannesburg", hour: "2-digit", minute: "2-digit" })}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <h4 className="text-sm font-semibold">Test a time slot</h4>
              <p className="text-xs text-muted-foreground">Add, move or delete an event in Google Calendar, then check the same time here (South African time).</p>
              <div className="flex flex-wrap items-end gap-2">
                <Input type="datetime-local" className="w-auto" value={when} onChange={(e) => setWhen(e.target.value)} />
                <Input type="number" className="w-24" min={5} max={480} value={mins} onChange={(e) => setMins(Number(e.target.value) || 60)} />
                <span className="text-sm text-muted-foreground">min</span>
                <Button size="sm" variant="outline" disabled={!when || checkMut.isPending} onClick={() => checkMut.mutate()}>Check</Button>
              </div>
              {result && (
                <ul className="text-sm">
                  <li>Website bookings: {result.website ? "Booked" : "Free"}</li>
                  <li>Google Calendar: {!result.googleReachable ? "Unreachable (booking would be refused)" : result.google ? "Busy" : "Free"}</li>
                  <li className="font-semibold">Patients see: {result.website || result.google || !result.googleReachable ? "UNAVAILABLE" : "AVAILABLE"}</li>
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
